import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/apiClient";
import { Combobox } from "../components/Combobox";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { MessageSquare, Send, Phone, AlertTriangle, User } from "lucide-react";
import { formatDateTime } from "../lib/dates";

function e164Preview(phone) {
  if (!phone) return null;
  const d = (phone.match(/\d/g) || []).join("");
  if (d.length === 10) return `+1${d}`;
  if (d.length === 11 && d.startsWith("1")) return `+${d}`;
  return phone.startsWith("+") ? phone : null;
}

function StatusPill({ status }) {
  const s = (status || "").toLowerCase();
  const cls = s === "failed"
    ? "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400"
    : s === "delivered" || s === "sent"
    ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400"
    : "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-400";
  return <span className={`inline-flex items-center rounded-sm px-1.5 py-0.5 text-xs font-medium capitalize ${cls}`}>{status || "queued"}</span>;
}

export default function Messaging() {
  const [config, setConfig] = useState(null);
  const [agents, setAgents] = useState([]);
  const [selected, setSelected] = useState(null); // {name, eid, phone, division}
  const [templateId, setTemplateId] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [log, setLog] = useState([]);

  const loadLog = () => api.get("/sms/messages", { params: { limit: 200 } }).then((r) => setLog(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/sms/config").then((r) => setConfig(r.data)).catch(() => setConfig({ configured: false }));
    api.get("/agents", { params: { division: "all", limit: 5000 } }).then((r) => setAgents(r.data || [])).catch(() => setAgents([]));
    loadLog();
  }, []);

  const options = useMemo(
    () => agents.map((a) => ({ value: a.name, label: a.name, eid: a.eid, phone: a.phone, division: a.division })),
    [agents]
  );

  const phoneE164 = selected ? e164Preview(selected.phone) : null;
  const canSend = config?.configured && selected && phoneE164 && body.trim() && !sending;

  const applyTemplate = (id) => {
    setTemplateId(id);
    const t = (config?.templates || []).find((x) => x.id === id);
    if (t) setBody(t.body);
  };

  const send = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      await api.post("/sms/send", {
        to: selected.phone,
        body: body.trim(),
        grievant_name: selected.name,
        eid: selected.eid,
      });
      toast.success(`Text sent to ${selected.name}`);
      setBody("");
      setTemplateId("");
      loadLog();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send text");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1200px]" data-testid="messaging-portal">
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1 flex items-center gap-2">
        <MessageSquare className="w-4 h-4" /> Messaging
      </p>
      <h1 className="font-head text-3xl sm:text-4xl font-extrabold tracking-tight">SMS Updates</h1>
      <p className="text-sm text-muted-foreground mt-1.5">
        Send grievance updates by text to the grievant's phone number on file.
        {config?.from_number ? <> Sending from <span className="font-mono">{config.from_number}</span>.</> : null}
      </p>

      {config && !config.configured && (
        <div className="mt-4 flex items-center gap-2 rounded-sm border border-amber-300 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-400 px-4 py-3 text-sm" data-testid="sms-not-configured">
          <AlertTriangle className="w-4 h-4" /> SMS is not configured on the server.
        </div>
      )}

      <div className="mt-8 grid lg:grid-cols-2 gap-8">
        {/* Compose */}
        <div className="border border-border rounded-sm bg-card p-5">
          <h2 className="font-head text-lg font-extrabold tracking-tight mb-4">Compose</h2>

          <label className="text-xs font-medium text-muted-foreground">Recipient (grievant)</label>
          <div className="mt-1.5">
            <Combobox
              testid="sms-recipient"
              value={selected?.name || ""}
              valueEid={selected?.eid}
              onChange={(v, opt) => setSelected(opt || null)}
              options={options}
              placeholder="Search grievant by name or EID…"
              searchPlaceholder="Type a name or EID…"
            />
          </div>

          {selected && (
            <div className="mt-2 text-sm flex items-center gap-2" data-testid="sms-recipient-phone">
              <Phone className="w-3.5 h-3.5 text-muted-foreground" />
              {phoneE164
                ? <span>{selected.phone} <span className="text-muted-foreground">→ {phoneE164}</span></span>
                : <span className="text-red-600 dark:text-red-400 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> No valid phone on file — cannot text this grievant.</span>}
            </div>
          )}

          <label className="text-xs font-medium text-muted-foreground mt-4 block">Template (optional)</label>
          <Select value={templateId} onValueChange={applyTemplate}>
            <SelectTrigger data-testid="sms-template" className="mt-1.5 rounded-sm bg-card">
              <SelectValue placeholder="Choose a template…" />
            </SelectTrigger>
            <SelectContent className="bg-card">
              {(config?.templates || []).map((t) => (
                <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[11px] text-muted-foreground mt-1">Edit placeholders like {"{case_no}"} and {"{status}"} before sending.</p>

          <label className="text-xs font-medium text-muted-foreground mt-4 block">Message</label>
          <textarea
            data-testid="sms-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={5}
            maxLength={1600}
            placeholder="Type your message…"
            className="mt-1.5 w-full rounded-sm border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-y"
          />
          <div className="flex items-center justify-between mt-1">
            <span className="text-[11px] text-muted-foreground">{body.length}/1600</span>
          </div>

          <Button data-testid="sms-send" onClick={send} disabled={!canSend} className="mt-3 w-full rounded-sm">
            <Send className="w-4 h-4 mr-2" /> {sending ? "Sending…" : "Send Text"}
          </Button>
        </div>

        {/* Log */}
        <div className="border border-border rounded-sm bg-card p-5">
          <h2 className="font-head text-lg font-extrabold tracking-tight mb-4">Message Log</h2>
          {log.length === 0 ? (
            <p className="text-sm text-muted-foreground">No messages sent yet.</p>
          ) : (
            <div className="space-y-3 max-h-[520px] overflow-y-auto pr-1" data-testid="sms-log">
              {log.map((m) => (
                <div key={m.id} className="border border-border rounded-sm p-3" data-testid={`sms-log-${m.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-medium flex items-center gap-1.5 min-w-0">
                      <User className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
                      <span className="truncate">{m.grievant_name || m.to}</span>
                    </div>
                    <StatusPill status={m.status} />
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5 font-mono">{m.to}</div>
                  <p className="text-sm mt-1.5 whitespace-pre-wrap">{m.body}</p>
                  {m.error && <p className="text-xs text-red-600 dark:text-red-400 mt-1">Error: {m.error}</p>}
                  <div className="text-[11px] text-muted-foreground mt-1.5">
                    {formatDateTime(m.sent_at)} · by {m.sent_by}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
